import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./NcdForm.css";
import { validateForm } from "../../../utils/validationUtils";
import { ncdAPI } from "../../../services/ncd.api";
import { useNCD } from "../hooks/useNCD";
import { formatToInputDate, formatToApiDate } from "../../../helpers/common-helper";

export const Hypertension = ({ individualId, htnScreenId, viewMode, reScreening, selectedUser, setUserError }) => {
    const { screeningData, setLoading, setScreeningData, setError, clearScreeningData } = useNCD();
    const today = new Date().toISOString().split("T")[0];
    const [formErrors, setFormErrors] = useState({});
    const htnscreen = screeningData[`hypertension_${individualId}`];

    const [htnscreenform, setHtnScreenform] = useState({
        diagnosedEarlier: false,
        underTreatment: true, // default to Yes
        referred: true, // default to Yes
        markKnownCaseFlag: false,
        systolic: "",
        diastolic: "",
        pulse: "",
        remarks: "",
        referredTo: "",
        referredToIfOther: null,
        date: "",
        examination: null,
        referral: null,
        currentScreeningId: null,
        screeningType: "FirstTime Screening"
    });

    useEffect(() => {

        const fetchHypertension = async () => {
            try {
                if (!individualId) return;

                setLoading(true);
                let hypertensionData;

                if (htnScreenId) {
                    hypertensionData = await ncdAPI.getScreeningData(500, individualId, htnScreenId);
                } else {
                    const screeningresult = await ncdAPI.getScreenDetails(individualId);
                    hypertensionData = await ncdAPI.getScreeningData(500, individualId, screeningresult.data[500].screeningId);
                    console.log(hypertensionData, "hypertensionData");
                }

                setScreeningData(`hypertension_${individualId}`, hypertensionData?.data);
                setLoading(false);
            } catch (error) {
                setError("Error fetching hypertension data");
                setLoading(false);
            }
        };

        if (viewMode === 'VIEW' && !htnscreen) {
            fetchHypertension();
        }

    }, [individualId, htnScreenId, viewMode]);

    useEffect(() => {
        if (htnscreen?.screenDateDisable && htnscreen?.date) {
            setHtnScreenform(prev => ({
                ...prev,
                date: formatToInputDate(htnscreen.date)
            }));
        } else {
            setHtnScreenform(prev => ({
                ...prev,
                date: today
            }));
        }
    }, [htnscreen]);

    const handleHtnChange = (e) => {
        const { name, value } = e.target;

        if (name === "date") {
            const selectedDate = new Date(value);
            const formattedDate = selectedDate
                .toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric"
                })
                .replace(/\//g, "-");

            setHtnScreenform(prev => ({
                ...prev,
                [name]: formattedDate
            }));
        } else {
            const newValue = value === "yes" ? true : value === "no" ? false : value;

            setHtnScreenform(prev => {
                const updatedForm = {
                    ...prev,
                    [name]: newValue,
                };

                // If "referred" is changed to "no", reset referredTo
                if (name === "referred" && value === "no") {
                    updatedForm.referredTo = "";
                }

                    // If "referredTo" is changed and it's not "Others", reset referredToIfOther

                if (name === "referredTo" && value !== "Others") {
                    updatedForm.referredToIfOther = "";
                }

                return updatedForm;
            });
        }

        setFormErrors(prev => ({
            ...prev,
            [name]: "",
        }));
    };

    const htnFormSchema = {
        date: { required: true, message: "This field is required." },
        systolic: {
            required: true,
            type: "number",
            min: 70,
            max: 250,
            message: "Enter systolic value between 70 and 140"
        },
        diastolic: {
            required: true,
            type: "number",
            min: 40,
            max: 140,
            message: "Enter diastolic value between 40 and 90"
            // customValidator: (value, htnscreenform) => {
            //   if (value >= htnscreenform.systolic) {
            //     return "Diastolic value should be smaller than systolic value.";
            //   }
            //   return null;
            // },
        },
        pulse: {
            required: true,
            type: "number",
            message: "This field is required.",
        },
        underTreatment: {
            customValidator: (value) => {
                if (value !== true && value !== false) {
                    return "Please select an option for under treatment.";
                }
                return null;
            },
        },
        referred: {
            customValidator: (value) => {
                if (value !== true && value !== false) {
                    return "Please select an option for referral.";
                }
                return null;
            },
        },
        referredTo: {
            required: (htnscreenform) => {
                return htnscreenform.referred === true; // Only required if referred is true
            },
            message: "Please select where to refer.",
        },
        referredToIfOther: {
            required: (htnscreenform) => {
                return (
                    htnscreenform.referred === true &&
                    htnscreenform.referredTo === "Others"
                );
            },
            message: "Please specify other referral."
        }
    };

    const handleSubmit = async (e) => {

        e.preventDefault();

        const errors = validateForm(htnscreenform, htnFormSchema);

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        if (!selectedUser) {
            setUserError(true);
            toast.error("Please select SHC staff", { className: "custom-toast-error" });
            return false;
        }

        try {
            setLoading(true);

            const payload = {
                ...htnscreenform,
                date: formatToApiDate(htnscreenform.date),
                currentScreeningId: htnScreenId || htnscreen?.currentScreeningId,
                createdUser: selectedUser,
                screeningANM: {
                    users: selectedUser,
                    anmValue: selectedUser,
                    viaTrained: true
                }
            };

            const response = await ncdAPI.submitHtn(500, individualId, payload);

            if (response.success) {
                // Clear Old data after successful submit
                clearScreeningData(`hypertension_${individualId}`);
                toast.success(`Hypertension data saved successfully`);
            } else {
                toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
            }
            setLoading(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setLoading(false);
        }
    };

    return (
        <>

            {viewMode === "ADD" ? (
                <>
                    <form onSubmit={(e) => handleSubmit(e)}>
                        <div className=" d-flex  align-items-center mb-4">
                            <span className="exam-label ml-5">Mark as a known case <span className="text-danger">*</span></span>
                            <div className="d-flex align-items-center" style={{ marginLeft: "9%" }}>
                                <label className="d-flex align-items-center gap-1">
                                    <input
                                        type="radio"
                                        name="underTreatment"
                                        value="yes"
                                        className="form-check-input"
                                        checked={htnscreenform.underTreatment === true}
                                        onChange={handleHtnChange}
                                    /> Yes
                                </label>
                                <label className="d-flex align-items-center gap-1">
                                    <input
                                        type="radio"
                                        name="underTreatment"
                                        value="no"
                                        checked={htnscreenform.underTreatment === false}
                                        onChange={handleHtnChange}
                                        className="form-check-input ml-20"
                                    /> No
                                </label>
                            </div>
                        </div>
                        <div className="personal-exam-row">

                            <div className="exam-item">
                                <span className="exam-label">Date <span className="text-danger">*</span></span>
                                <input
                                    type="date"
                                    name="date"
                                    className="exam-textarea"
                                    value={htnscreenform.date}
                                    onChange={handleHtnChange}
                                    max={today}
                                    disabled={htnscreen?.screenDateDisable}
                                />
                                {formErrors.date && <p className="error">{formErrors.date}</p>}
                            </div>
                            <div className="exam-item">
                                <span className="exam-label">Systolic (mmHg) <span className="text-danger">*</span></span>
                                <input type="text" className="exam-textarea" name="systolic" maxLength={3} onChange={handleHtnChange}></input>
                                {formErrors.systolic && <p className="error">{formErrors.systolic}</p>}
                            </div>
                            <div className="exam-item">
                                <span className="exam-label">Diastolic (mmHg) <span className="text-danger">*</span></span>
                                <input type="text" className="exam-textarea" name="diastolic" maxLength={3} onChange={handleHtnChange}></input>
                                {formErrors.diastolic && <p className="error">{formErrors.diastolic}</p>}
                            </div>
                            <div className="exam-item">
                                <span className="exam-label">Pulse Rate </span>
                                <input type="text" className="exam-textarea" name="pulse" maxLength={3} onChange={handleHtnChange}></input>
                                {formErrors.pulse && <p className="error">{formErrors.pulse}</p>}
                            </div>

                            <div className="exam-item">
                                <span className="exam-label">Refer </span>
                                <div>
                                    {htnscreenform.underTreatment === true ? (
                                        <label className="d-flex align-items-center gap-1">
                                            <input
                                                type="radio"
                                                name="referred"
                                                value="yes"
                                                className="exam_radio form-check-input"
                                                checked={htnscreenform.referred === true}
                                                onChange={handleHtnChange}
                                            /> Yes
                                        </label>
                                    ) : (
                                        // Both Yes and No radios
                                        <>
                                            <label className="align-items-center">
                                                <input
                                                    type="radio"
                                                    name="referred"
                                                    value="yes"
                                                    className="exam_radio form-check-input"
                                                    checked={htnscreenform.referred === true}
                                                    onChange={handleHtnChange}
                                                /> Yes
                                            </label>
                                            <label className="align-items-center">
                                                <input
                                                    type="radio"
                                                    name="referred"
                                                    value="no"
                                                    className="exam_radio ml-20 form-check-input"
                                                    checked={htnscreenform.referred === false}
                                                    onChange={handleHtnChange}
                                                /> No
                                            </label>
                                        </>
                                    )}
                                </div>
                                {formErrors.referred && <p className="error">{formErrors.referred}</p>}
                            </div>

                            <div className="exam-item">
                                <span className="exam-label">Refer To </span>
                                <select
                                    className="exam-dropdown"
                                    name="referredTo"
                                    onChange={handleHtnChange}
                                    disabled={htnscreenform.referred !== true}
                                    value={htnscreenform.referredTo || ""}
                                >
                                    <option value="">Select</option>
                                    <option value="PHC">PHC</option>
                                    <option value="CHC">CHC</option>
                                    <option value="DH">District Hospital</option>
                                    <option value="Others">Other</option>
                                </select>
                                {formErrors.referredTo && <p className="error">{formErrors.referredTo}</p>}
                            </div>
                            <div className="exam-item">
                                <span className="exam-label">If other, specify  { htnscreenform.referredTo === "Others" && <span className="text-danger">*</span> }</span>
                                <input
                                    type="text"
                                    className="exam-textarea"
                                    name="referredToIfOther"
                                    value={htnscreenform.referredToIfOther || ""}
                                    onChange={handleHtnChange}
                                    disabled={
                                        htnscreenform.referred !== true ||
                                        htnscreenform.referredTo !== "Others"
                                    }
                                />

                                {formErrors.referredToIfOther && htnscreenform.referredTo === "Others" && (
                                    <p className="error">{formErrors.referredToIfOther}</p>
                                )}
                            </div>

                        </div>
                        <div className="row">

                            <div className="exam-item col-md-8">
                                <span className="exam-label">Remarks </span>
                                <input type="text" className="exam-textarea" name="remarks" onChange={handleHtnChange}></input>
                            </div>

                        </div>
                        <div className="save-button-container">
                            <button type="submit" className="save-button">Save</button>
                        </div>

                    </form>

                </>

            ) : (

                <div className="htn-screen table-responsive">

                    <table className="table table-responsive custom-striped">
                        <thead className="table-head">
                            <tr>
                                <th>Date</th>
                                <th>Systolic (mmHg)</th>
                                <th>Diastolic (mmHg)</th>
                                <th>Pulse Rate</th>
                                <th>Currently Under Treatment</th>
                                <th>Refer</th>
                                <th>Refer To</th>
                                <th>Refer To Others</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>

                            {htnscreen?.systolic ? (
                                <tr>
                                    <td>{htnscreen?.date ?? '--'}</td>
                                    <td>{htnscreen?.systolic ?? '--'}</td>
                                    <td>{htnscreen?.diastolic ?? '--'}</td>
                                    <td>{htnscreen?.pulse ?? '--'}</td>
                                    <td>{htnscreen?.underTreatment ? 'Yes' : 'No'}</td>
                                    <td>{htnscreen?.referred ? 'Yes' : 'No'}</td>
                                    <td>{htnscreen?.referredTo ?? '--'}</td>
                                    <td>{htnscreen?.referredToIfOther ?? '--'}</td>
                                    <td>{htnscreen?.remarks ?? '--'}</td>
                                </tr>
                            ) : (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center' }}>No record found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            )}
            <div className="separator"></div>
        </>
    );
};